import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAction, useGlobalAction } from "@gadgetinc/react";
import { api } from "../../../api";
import { safeArrayConversion, safeArrayMap } from "./configHelpers";
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  Divider,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  IconButton,
  Collapse,
  Heading,
  Flex,
} from "@chakra-ui/react";
import { EditIcon, ChevronDownIcon, ChevronUpIcon, AddIcon, DeleteIcon } from "@chakra-ui/icons";

export default function MemberManagementModal({ isOpen, onClose, cohort, onMemberUpdate }) {
  const normalizeMembershipRole = useCallback((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "instructor" || normalized === "staff" || normalized === "student") {
      return normalized;
    }
    return "student";
  }, []);

  const inferMembershipRoleFromInstitutionRole = useCallback((institutionRole) => {
    const roleValues = Array.isArray(institutionRole)
      ? institutionRole
      : [institutionRole];
    const normalizedRoles = roleValues
      .map((role) => String(role || "").trim().toLowerCase())
      .filter(Boolean);

    if (
      normalizedRoles.includes("instructor") ||
      normalizedRoles.includes("didactic") ||
      normalizedRoles.includes("lab") ||
      normalizedRoles.includes("clinical") ||
      normalizedRoles.includes("field")
    ) return "instructor";
    if (
      normalizedRoles.includes("administrator") ||
      normalizedRoles.includes("staff") ||
      normalizedRoles.includes("scheduler")
    ) {
      return "staff";
    }
    return "student";
  }, []);

  const [{ fetching: updatingMembership }, updateCohortMembership] = useAction(api.cohortMembership.update);
  const [{ fetching: fetchingManagedCohortData }, getCohortManagementData] = useGlobalAction(api.getCohortManagementData);

  const [searchTerm, setSearchTerm] = useState("");
  const [membersToAdd, setMembersToAdd] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [fallbackManagementData, setFallbackManagementData] = useState(null);
  const [fallbackManagementError, setFallbackManagementError] = useState(null);
  const [loadingFallbackManagementData, setLoadingFallbackManagementData] = useState(false);
  const toast = useToast();

  const sortUsers = useCallback((users = []) => (
    [...users].sort((a, b) => {
      const aLast = String(a?.lastName || "").toLowerCase();
      const bLast = String(b?.lastName || "").toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);

      const aFirst = String(a?.firstName || "").toLowerCase();
      const bFirst = String(b?.firstName || "").toLowerCase();
      if (aFirst !== bFirst) return aFirst.localeCompare(bFirst);

      return String(a?.email || "").toLowerCase().localeCompare(String(b?.email || "").toLowerCase());
    })
  ), []);

  const fetchAllPages = useCallback(async (fetchPage, pageSize = 250) => {
    const records = [];
    let after;

    while (true) {
      const page = await fetchPage({ first: pageSize, after });
      records.push(...(Array.isArray(page) ? page : []));
      if (!page?.hasNextPage || !page?.endCursor) break;
      after = page.endCursor;
    }

    return records;
  }, []);

  const fetchFallbackManagementData = useCallback(async () => {
    if (!cohort?.id || !cohort?.institution) {
      return {
        cohort,
        memberships: [],
        institutionUsers: [],
        legacyCohortUsers: []
      };
    }

    const [memberships, institutionUsers] = await Promise.all([
      fetchAllPages(({ first, after }) =>
        api.cohortMembership.findMany({
          filter: { cohortId: { equals: cohort.id } },
          first,
          after,
          select: {
            id: true,
            userId: true,
            primaryRole: true,
            subRoles: true,
            active: true,
            notificationPreferences: true,
            hasFisdapReviewAccess: true,
            hasScenarioGeneratorAccess: true,
            assignedAt: true,
            user: {
              id: true,
              firstName: true,
              lastName: true,
              email: true,
              cohortId: true,
              institutionRole: true,
              individualScenarioAccess: true,
              individualScenarioGeneratorAccess: true,
              scenarioCreditSources: true,
              activeCohorts: true
            }
          }
        })
      ),
      fetchAllPages(({ first, after }) =>
        api.user.findMany({
          filter: { institution: { equals: cohort.institution } },
          first,
          after,
          select: {
            id: true,
            firstName: true,
            lastName: true,
            email: true,
            cohortId: true,
            institutionRole: true,
            individualScenarioAccess: true,
            individualScenarioGeneratorAccess: true,
            scenarioCreditSources: true,
            activeCohorts: true
          }
        })
      )
    ]);

    const legacyCohortUsers = institutionUsers.filter((user) => {
      if (!user?.id) return false;
      if (user.cohortId === cohort.id) return true;
      return Array.isArray(user.activeCohorts) && user.activeCohorts.includes(cohort.id);
    });

    return {
      cohort,
      memberships,
      institutionUsers: sortUsers(institutionUsers),
      legacyCohortUsers: sortUsers(legacyCohortUsers)
    };
  }, [cohort, fetchAllPages, sortUsers]);

  const getUserDisplayName = useCallback((user) => {
    const fullName = [user?.firstName, user?.lastName]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ");
    if (fullName) return fullName;
    if (user?.email) return user.email;
    if (user?.id) return `User ${user.id}`;
    return "Unknown user";
  }, []);

  const userHasRenderableIdentity = useCallback((user) => (
    Boolean(
      user?.id ||
      user?.email ||
      String(user?.firstName || "").trim() ||
      String(user?.lastName || "").trim()
    )
  ), []);

  const activeManagementData = fallbackManagementData;
  const activeManagementError = fallbackManagementError;
  const loadingManagement = loadingFallbackManagementData || fetchingManagedCohortData;

  const memberships = useMemo(() => (
    Array.isArray(activeManagementData?.memberships) ? activeManagementData.memberships : []
  ), [activeManagementData?.memberships]);

  const availableUsers = useMemo(() => (
    Array.isArray(activeManagementData?.institutionUsers) ? activeManagementData.institutionUsers : []
  ), [activeManagementData?.institutionUsers]);

  const legacyCohortUsers = useMemo(() => (
    Array.isArray(activeManagementData?.legacyCohortUsers) ? activeManagementData.legacyCohortUsers : []
  ), [activeManagementData?.legacyCohortUsers]);

  const refreshManagementData = useCallback(async () => {
    if (!cohort?.id) return;
    setFallbackManagementError(null);
    setLoadingFallbackManagementData(true);
    try {
      let data = null;

      try {
        data = await getCohortManagementData({ cohortId: cohort.id });
      } catch (_error) {
        data = null;
      }

      if (!data?.cohort || !Array.isArray(data?.memberships)) {
        data = await fetchFallbackManagementData();
      }

      setFallbackManagementData(data);
      setFallbackManagementError(null);
    } catch (error) {
      setFallbackManagementError(error);
      setFallbackManagementData(null);
    } finally {
      setLoadingFallbackManagementData(false);
      setLastRefreshTime(new Date());
    }
  }, [cohort?.id, fetchFallbackManagementData, getCohortManagementData]);

  const buildDefaultScenarioAccess = useCallback((roleValue) => {
    const normalizedRole = normalizeMembershipRole(roleValue);
    return (
      cohort?.cohortScenarioGeneratorAccess === true &&
      cohort?.grantScenarioAccessToInstructors === true &&
      normalizedRole === "instructor"
    );
  }, [
    cohort?.cohortScenarioGeneratorAccess,
    cohort?.grantScenarioAccessToInstructors,
    normalizeMembershipRole
  ]);

  const buildDefaultNotificationPreferences = useCallback(() => ({
    notifyOnPass: false,
    notifyOnFail: true,
    notifyOnNeedsReview: true,
    notifyOnXphiPass: false,
    notifyOnXphiFail: true,
    notifyOnDocSubmission: cohort?.enableClinicalDocNotifications === true,
    emailFrequency: "immediate"
  }), [cohort?.enableClinicalDocNotifications]);

  useEffect(() => {
    if (isOpen) {
      refreshManagementData();
    }
  }, [isOpen, refreshManagementData]);

  const normalizedMemberships = useMemo(() => (
    Array.isArray(memberships) ? memberships.filter(Boolean) : []
  ), [memberships]);

  const activeMemberships = useMemo(() => (
    normalizedMemberships.filter((membership) => membership?.active !== false)
  ), [normalizedMemberships]);

  const effectiveMemberships = useMemo(() => {
    const mergedMemberships = [...activeMemberships];
    const existingUserIds = new Set(
      activeMemberships
        .map((membership) => membership?.user?.id)
        .filter(Boolean)
    );

    const legacyCandidateMap = new Map();
    const legacyUsers = Array.isArray(legacyCohortUsers) ? legacyCohortUsers : [];
    for (const legacyUser of legacyUsers) {
      const isLegacyMember =
        legacyUser?.cohortId === cohort?.id ||
        (Array.isArray(legacyUser?.activeCohorts) && legacyUser.activeCohorts.includes(cohort?.id));
      if (isLegacyMember && legacyUser?.id) {
        legacyCandidateMap.set(legacyUser.id, legacyUser);
      }
    }

    const institutionUsers = Array.isArray(availableUsers) ? availableUsers : [];
    for (const institutionUser of institutionUsers) {
      const isLegacyMember =
        institutionUser?.cohortId === cohort?.id ||
        (Array.isArray(institutionUser?.activeCohorts) && institutionUser.activeCohorts.includes(cohort?.id));
      if (isLegacyMember && institutionUser?.id && !legacyCandidateMap.has(institutionUser.id)) {
        legacyCandidateMap.set(institutionUser.id, institutionUser);
      }
    }

    for (const legacyUser of legacyCandidateMap.values()) {
      if (!legacyUser?.id || existingUserIds.has(legacyUser.id)) continue;

      const inferredRole = inferMembershipRoleFromInstitutionRole(legacyUser.institutionRole);
      mergedMemberships.push({
        id: `legacy-${legacyUser.id}`,
        primaryRole: inferredRole,
        subRoles: [],
        notificationPreferences: buildDefaultNotificationPreferences(),
        hasFisdapReviewAccess: inferredRole === "instructor",
        hasScenarioGeneratorAccess: buildDefaultScenarioAccess(inferredRole),
        active: true,
        user: legacyUser,
        isLegacyFallback: true
      });
    }

    return mergedMemberships;
  }, [
    activeMemberships,
    legacyCohortUsers,
    availableUsers,
    cohort?.id,
    inferMembershipRoleFromInstitutionRole,
    buildDefaultNotificationPreferences,
    buildDefaultScenarioAccess,
    refreshKey
  ]);

  const knownUsersById = useMemo(() => {
    const usersById = new Map();
    if (Array.isArray(availableUsers)) {
      for (const user of availableUsers) {
        if (user?.id) usersById.set(user.id, user);
      }
    }
    if (Array.isArray(legacyCohortUsers)) {
      for (const user of legacyCohortUsers) {
        if (user?.id && !usersById.has(user.id)) usersById.set(user.id, user);
      }
    }
    for (const membership of effectiveMemberships) {
      if (membership?.user?.id && !usersById.has(membership.user.id)) {
        usersById.set(membership.user.id, membership.user);
      }
    }
    return usersById;
  }, [availableUsers, legacyCohortUsers, effectiveMemberships]);

  const getUserRole = useCallback((user) => {
    if (!user) return "Not a member";
    const membership = effectiveMemberships.find((m) => m?.user?.id === user.id);
    if (!membership) return "Not a member";
    const subRoles = safeArrayConversion(membership.subRoles);
    if (subRoles.length > 0) {
      return `${membership.primaryRole} (${subRoles.join(", ")})`;
    }
    return membership.primaryRole;
  }, [effectiveMemberships]);

  const visibleMemberships = useMemo(() => (
    effectiveMemberships.filter((membership) => userHasRenderableIdentity(membership?.user))
  ), [effectiveMemberships, userHasRenderableIdentity]);

  const orphanMemberships = useMemo(() => (
    effectiveMemberships.filter((membership) => !userHasRenderableIdentity(membership?.user))
  ), [effectiveMemberships, userHasRenderableIdentity]);

  const currentMembers = useMemo(() => (
    visibleMemberships.map((membership) => membership.user).filter(Boolean)
  ), [visibleMemberships]);

  const instructors = useMemo(() => (
    visibleMemberships
      .filter((membership) => normalizeMembershipRole(membership?.primaryRole) === "instructor")
      .map((membership) => membership.user)
      .filter(Boolean)
  ), [visibleMemberships, normalizeMembershipRole]);

  const staff = useMemo(() => (
    visibleMemberships
      .filter((membership) => normalizeMembershipRole(membership?.primaryRole) === "staff")
      .map((membership) => membership.user)
      .filter(Boolean)
  ), [visibleMemberships, normalizeMembershipRole]);

  const students = useMemo(() => (
    visibleMemberships
      .filter((membership) => normalizeMembershipRole(membership?.primaryRole) === "student")
      .map((membership) => membership.user)
      .filter(Boolean)
  ), [visibleMemberships, normalizeMembershipRole]);

  const availableToAdd = useMemo(() => {
    if (!Array.isArray(availableUsers) || !Array.isArray(currentMembers)) return [];
    const currentMemberIds = new Set(currentMembers.map(m => m.id));
    return availableUsers.filter(user =>
      user &&
      !currentMemberIds.has(user.id) &&
      (searchTerm === "" ||
       user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       user.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [availableUsers, currentMembers, searchTerm, refreshKey]);

  const ensureMembership = useCallback(async (userId, roleHint = "student") => {
    const userRecord = knownUsersById.get(userId);
    const fallbackRole = inferMembershipRoleFromInstitutionRole(userRecord?.institutionRole);
    const normalizedRole = normalizeMembershipRole(roleHint || fallbackRole);
    const existingMembership = normalizedMemberships.find((membership) => membership?.user?.id === userId);
    if (existingMembership) {
      if (existingMembership.active === false) {
        const existingPreferences =
          existingMembership.notificationPreferences && typeof existingMembership.notificationPreferences === "object"
            ? existingMembership.notificationPreferences
            : {};
        const defaultPreferences = buildDefaultNotificationPreferences();
        const reactivatedPayload = {
          active: true,
          primaryRole: normalizedRole,
          subRoles: existingMembership.subRoles || [],
          hasFisdapReviewAccess: normalizedRole === "instructor",
          hasScenarioGeneratorAccess: buildDefaultScenarioAccess(normalizedRole),
          notificationPreferences: {
            notifyOnPass: existingPreferences.notifyOnPass ?? defaultPreferences.notifyOnPass,
            notifyOnFail: existingPreferences.notifyOnFail ?? defaultPreferences.notifyOnFail,
            notifyOnNeedsReview:
              existingPreferences.notifyOnNeedsReview ?? defaultPreferences.notifyOnNeedsReview,
            notifyOnXphiPass:
              existingPreferences.notifyOnXphiPass ?? defaultPreferences.notifyOnXphiPass,
            notifyOnXphiFail:
              existingPreferences.notifyOnXphiFail ?? defaultPreferences.notifyOnXphiFail,
            notifyOnDocSubmission: defaultPreferences.notifyOnDocSubmission,
            emailFrequency: existingPreferences.emailFrequency || defaultPreferences.emailFrequency
          }
        };
        await updateCohortMembership({ id: existingMembership.id, ...reactivatedPayload });
        return { ...existingMembership, ...reactivatedPayload };
      }
      return existingMembership;
    }

    const createdMembership = await api.cohortMembership.create({
      user: { _link: userId },
      cohort: { _link: cohort.id },
      primaryRole: normalizedRole,
      subRoles: [],
      active: true,
      assignedAt: new Date(),
      hasFisdapReviewAccess: normalizedRole === "instructor",
      hasScenarioGeneratorAccess: buildDefaultScenarioAccess(normalizedRole),
      notificationPreferences: buildDefaultNotificationPreferences()
    });

    return createdMembership;
  }, [
    normalizedMemberships,
    knownUsersById,
    inferMembershipRoleFromInstitutionRole,
    normalizeMembershipRole,
    cohort?.id,
    updateCohortMembership,
    buildDefaultScenarioAccess,
    buildDefaultNotificationPreferences
  ]);

  const resolveMembershipForMutation = useCallback(async (membershipId, userId, roleHint = "student") => {
    if (membershipId && !String(membershipId).startsWith("legacy-")) {
      const existingById = normalizedMemberships.find((membership) => membership.id === membershipId);
      if (existingById) return existingById;
    }
    return ensureMembership(userId, roleHint);
  }, [normalizedMemberships, ensureMembership]);

  const handleAddMembers = async () => {
    if (membersToAdd.length === 0) return;
    try {
      for (const userId of membersToAdd) {
        const userRecord = knownUsersById.get(userId);
        const inferredRole = inferMembershipRoleFromInstitutionRole(userRecord?.institutionRole);
        await ensureMembership(userId, inferredRole);
      }
      toast({ title: "Members Added", description: `Successfully added ${membersToAdd.length} member(s) to the cohort.`, status: "success", duration: 3000, isClosable: true });
      setMembersToAdd([]);
      setSearchTerm("");
      await refreshManagementData();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
    } catch (error) {
      toast({ title: "Error Adding Members", description: error.message || "Failed to add members to cohort", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const membership = normalizedMemberships.find((m) => m?.user?.id === userId && m?.active !== false)
        || normalizedMemberships.find((m) => m?.user?.id === userId);
      if (membership && membership.active !== false) {
        await api.cohortMembership.delete(membership.id);
      } else {
        // Legacy fallback users may still be linked only through user.cohort.
        const userRecord = knownUsersById.get(userId);
        const activeCohorts = Array.isArray(userRecord?.activeCohorts)
          ? userRecord.activeCohorts.filter(Boolean)
          : [];
        const nextActiveCohorts = activeCohorts.filter((id) => id !== cohort?.id);
        const wasPrimaryCohort = userRecord?.cohortId === cohort?.id;
        const nextPrimaryCohortId = wasPrimaryCohort ? (nextActiveCohorts[0] || null) : userRecord?.cohortId || null;

        if (!wasPrimaryCohort && activeCohorts.length === nextActiveCohorts.length) {
          return;
        }

        await api.user.update(userId, {
          cohort: nextPrimaryCohortId ? { _link: nextPrimaryCohortId } : null,
          activeCohorts: nextActiveCohorts
        });
      }
      await refreshManagementData();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
      setRefreshKey(prev => prev + 1);
      toast({ title: "Member Removed", description: "Successfully removed member from the cohort.", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Error Removing Member", description: error.message || "Failed to remove member from cohort", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleRoleChange = async (userId, newRole, specializations = []) => {
    try {
      const membership = await ensureMembership(userId, newRole);
      const normalizedRole = normalizeMembershipRole(newRole);
      const currentRole = normalizeMembershipRole(membership?.primaryRole);
      const roleChanged = currentRole !== normalizedRole;
      const isInstructor = normalizedRole === "instructor";
      const nextScenarioAccess = roleChanged
        ? buildDefaultScenarioAccess(normalizedRole)
        : (
          membership?.hasScenarioGeneratorAccess === true ||
          buildDefaultScenarioAccess(normalizedRole)
        );
      await api.cohortMembership.update(membership.id, {
        primaryRole: normalizedRole,
        subRoles: specializations,
        hasFisdapReviewAccess: isInstructor ? true : false,
        hasScenarioGeneratorAccess: nextScenarioAccess
      });
      await refreshManagementData();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
      toast({ title: "Role Updated", description: "Successfully updated member role.", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      toast({ title: "Error Updating Role", description: error.message || "Failed to update member role", status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleRemoveOrphanMembership = async (membershipId) => {
    if (!membershipId || String(membershipId).startsWith("legacy-")) return;
    try {
      await api.cohortMembership.delete(membershipId);
      await refreshManagementData();
      if (onMemberUpdate) setTimeout(() => onMemberUpdate(), 500);
      setRefreshKey(prev => prev + 1);
      toast({
        title: "Orphan Membership Removed",
        description: "Removed invalid cohort membership record.",
        status: "success",
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: "Error Removing Orphan Membership",
        description: error.message || "Failed to remove orphan membership",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleUpdateNotificationPreferences = async (membershipId, userId, preferences) => {
    try {
      const membership = await resolveMembershipForMutation(membershipId, userId, "instructor");
      await updateCohortMembership({ id: membership.id, notificationPreferences: preferences });
      toast({ title: 'Preferences Updated', description: 'Notification preferences have been saved', status: 'success', duration: 3000, isClosable: true });
      await refreshManagementData();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update preferences', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleUpdateFisdapAccess = async (membershipId, userId, hasAccess) => {
    try {
      const membership = await resolveMembershipForMutation(membershipId, userId, "instructor");
      await updateCohortMembership({ id: membership.id, hasFisdapReviewAccess: hasAccess });
      toast({ title: 'FISDAP Access Updated', description: hasAccess ? 'FISDAP review access granted' : 'FISDAP review access revoked', status: 'success', duration: 3000, isClosable: true });
      await refreshManagementData();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update FISDAP access', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleUpdateScenarioAccess = async (membershipId, userId, hasAccess) => {
    try {
      const currentMembership = effectiveMemberships.find((m) => m?.user?.id === userId);
      const membership = await resolveMembershipForMutation(
        membershipId,
        userId,
        currentMembership?.primaryRole || "student"
      );
      await updateCohortMembership({
        id: membership.id,
        hasScenarioGeneratorAccess: Boolean(hasAccess)
      });
      toast({
        title: "Scenario Generator Access Updated",
        description: hasAccess
          ? "Scenario generator access granted for this cohort"
          : "Scenario generator access revoked for this cohort",
        status: "success",
        duration: 3000,
        isClosable: true
      });
      await refreshManagementData();
      if (onMemberUpdate) await onMemberUpdate();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to update scenario generator access', status: 'error', duration: 5000, isClosable: true });
    }
  };

  const toggleNotificationSettings = (userId) => {
    const newExpanded = new Set(expandedNotifications);
    if (newExpanded.has(userId)) newExpanded.delete(userId);
    else newExpanded.add(userId);
    setExpandedNotifications(newExpanded);
  };

  // Render a member row for a given role section
  const renderMemberRow = (member, roleValue, bgColor) => {
    const membership = effectiveMemberships.find((m) => m?.user?.id === member.id);
    const memberDisplayName = getUserDisplayName(member);
    const specializations = safeArrayConversion(membership?.subRoles);
    const rawNotificationPrefs =
      membership?.notificationPreferences && typeof membership.notificationPreferences === "object"
        ? membership.notificationPreferences
        : {};
    const notificationPrefs = {
      ...buildDefaultNotificationPreferences(),
      ...rawNotificationPrefs
    };
    const isNotificationExpanded = expandedNotifications.has(member.id);
    const isLegacyFallback = membership?.isLegacyFallback === true;
    const hasFisdapAccess = membership?.hasFisdapReviewAccess === true;
    const hasDirectIndividualAccess =
      member.individualScenarioAccess === true || member.individualScenarioGeneratorAccess === true;
    const hasCohortScenarioAccess =
      (membership?.hasScenarioGeneratorAccess ?? false) === true ||
      (
        normalizeMembershipRole(membership?.primaryRole) === "instructor" &&
        cohort?.cohortScenarioGeneratorAccess === true &&
        cohort?.grantScenarioAccessToInstructors === true
      );
    const hasScenarioAccess = hasCohortScenarioAccess || hasDirectIndividualAccess;
    const scenarioToggleDisabled = hasDirectIndividualAccess && !hasCohortScenarioAccess;

    const roleOptions = ['Instructor', 'Staff', 'Student'].filter(r => r !== roleValue);

    return (
      <Box key={`${roleValue.toLowerCase()}-${member.id}`}>
        <Flex p={3} bg={bgColor} borderRadius="md" align="center">
          <VStack align="start" spacing={0} flex="1">
            <Text fontWeight="bold">{memberDisplayName}</Text>
            {member?.email && member.email !== memberDisplayName && (
              <Text fontSize="sm" color="gray.300">{member.email}</Text>
            )}
            <HStack spacing={1} mt={1} flexWrap="wrap">
              {isLegacyFallback && (
                <Badge size="xs" colorScheme="orange" variant="outline">Legacy Cohort Link</Badge>
              )}
              {roleValue === 'Instructor' && (
                specializations.length > 0 ? specializations.map(spec => (
                  <Badge key={spec} size="xs" colorScheme="blue">{spec}</Badge>
                )) : (
                  <Badge size="xs" colorScheme="gray">No specializations</Badge>
                )
              )}
              {roleValue === 'Staff' && (
                specializations.length > 0 ? specializations.map(role => (
                  <Badge key={role} size="xs" colorScheme="green">{role}</Badge>
                )) : (
                  <Badge size="xs" colorScheme="gray">No sub-roles</Badge>
                )
              )}
              {roleValue === 'Student' && (
                <Text fontSize="xs" color="gray.400">{getUserRole(member)}</Text>
              )}
              {hasFisdapAccess && <Badge size="xs" colorScheme="green">FISDAP Review</Badge>}
              {(hasCohortScenarioAccess || hasDirectIndividualAccess) && (
                <HStack spacing={1}>
                  {hasCohortScenarioAccess && <Badge size="xs" colorScheme="purple">Cohort Scenario Access</Badge>}
                  {hasDirectIndividualAccess && <Badge size="xs" colorScheme="green">Individual Scenario Access</Badge>}
                  {roleValue === 'Instructor' && cohort?.cohortScenarioGeneratorAccess === true && cohort?.grantScenarioAccessToInstructors === true && (
                    <Badge size="xs" colorScheme="purple" variant="outline">Auto-granted (Cohort Setting)</Badge>
                  )}
                </HStack>
              )}
            </HStack>
            {member.scenarioCreditSources && member.scenarioCreditSources.length > 0 && (
              <Text fontSize="xs" color="gray.400">Credits: {member.scenarioCreditSources.join(', ')}</Text>
            )}
          </VStack>
          <VStack align="start" spacing={2}>
            <Select
              value={roleValue}
              onChange={(e) => {
                if (e.target.value !== roleValue) {
                  handleRoleChange(member.id, e.target.value, e.target.value === roleValue ? specializations : []);
                }
              }}
              size="sm" bg="gray.600" borderColor="gray.500" minW="120px"
            >
              <option value={roleValue}>{roleValue}</option>
              {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </Select>
            {roleValue === 'Instructor' && (
              <VStack align="start" spacing={1} pl={2}>
                <Text fontSize="xs" color="gray.400">Specializations:</Text>
                {['didactic', 'lab', 'clinical', 'field'].map(spec => (
                  <Checkbox key={spec} size="sm"
                    isChecked={specializations.includes(spec)}
                    onChange={(e) => {
                      const newSpecs = e.target.checked
                        ? [...specializations, spec]
                        : specializations.filter(s => s !== spec);
                      handleRoleChange(member.id, 'Instructor', newSpecs);
                    }}
                  >
                    <Text fontSize="xs">{spec.charAt(0).toUpperCase() + spec.slice(1)}</Text>
                  </Checkbox>
                ))}
              </VStack>
            )}
            <VStack align="start" spacing={1} pl={2} mt={roleValue === 'Instructor' ? 2 : 0}>
              {roleValue === 'Instructor' && <Text fontSize="xs" color="gray.400">Access:</Text>}
              <Checkbox size="sm" isChecked={hasFisdapAccess}
                onChange={(e) => handleUpdateFisdapAccess(membership?.id, member.id, e.target.checked)} colorScheme="green">
                <Text fontSize="xs">FISDAP Review{roleValue !== 'Instructor' ? '' : ' Access'}</Text>
              </Checkbox>
              <Checkbox size="sm" isChecked={hasScenarioAccess}
                isDisabled={scenarioToggleDisabled}
                onChange={(e) => handleUpdateScenarioAccess(membership?.id, member.id, e.target.checked)} colorScheme="purple">
                <Text fontSize="xs">Scenario Generator Access</Text>
              </Checkbox>
              {scenarioToggleDisabled && (
                <Text fontSize="xs" color="gray.400">
                  Individual grant active; update from Individual Access section.
                </Text>
              )}
            </VStack>
            <HStack spacing={2}>
              {roleValue === 'Instructor' && (
                <Button size="xs"
                  leftIcon={isNotificationExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={() => toggleNotificationSettings(member.id)}
                  colorScheme="teal" variant="outline">
                  Notifications
                </Button>
              )}
              <IconButton icon={<DeleteIcon />} onClick={() => handleRemoveMember(member.id)}
                colorScheme="red" variant="ghost" size="sm" />
            </HStack>
          </VStack>
        </Flex>

        {/* Notification settings collapse (instructors only) */}
        {roleValue === 'Instructor' && (
          <Collapse in={isNotificationExpanded}>
            <Box p={4} bg="blue.800" borderRadius="md" mt={2} ml={4}>
              <VStack spacing={3} align="stretch">
                <Text fontSize="sm" fontWeight="bold" color="white">
                  Notification Settings for {memberDisplayName}
                </Text>
                <VStack spacing={2} align="stretch">
                  {[
                    { key: 'notifyOnPass', label: 'Notify on student shift pass', scheme: 'green' },
                    { key: 'notifyOnFail', label: 'Notify on student shift fail', scheme: 'red' },
                    { key: 'notifyOnXphiPass', label: 'Notify on XPHI clear', scheme: 'green' },
                    { key: 'notifyOnXphiFail', label: 'Notify on XPHI flagged/errors', scheme: 'red' },
                    { key: 'notifyOnNeedsReview', label: 'Notify on student shift needs review', scheme: 'yellow' },
                    { key: 'notifyOnDocSubmission', label: 'Notify on clinical document submission', scheme: 'blue' },
                  ].map(({ key, label, scheme }) => (
                    <Checkbox key={key}
                      isChecked={notificationPrefs[key] || false}
                      onChange={(e) => handleUpdateNotificationPreferences(membership?.id, member.id, { ...notificationPrefs, [key]: e.target.checked })}
                      size="sm" colorScheme={scheme}>
                      <Text fontSize="sm">{label}</Text>
                    </Checkbox>
                  ))}
                </VStack>
                <FormControl>
                  <FormLabel fontSize="sm" color="white">Email Notification Frequency</FormLabel>
                  <Select
                    value={notificationPrefs.emailFrequency || 'immediate'}
                    onChange={(e) => handleUpdateNotificationPreferences(membership?.id, member.id, { ...notificationPrefs, emailFrequency: e.target.value })}
                    size="sm" bg="gray.600" borderColor="gray.500" color="white">
                    <option value="immediate">Immediate</option>
                    <option value="daily">Daily Digest</option>
                    <option value="weekly">Weekly Digest</option>
                    <option value="none">No Emails</option>
                  </Select>
                </FormControl>
                {updatingMembership && (
                  <HStack spacing={2}>
                    <Spinner size="xs" color="blue.400" />
                    <Text fontSize="xs" color="gray.300">Saving preferences...</Text>
                  </HStack>
                )}
              </VStack>
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Manage Cohort Members - {cohort?.displayName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="md">Current Members ({currentMembers.length})</Heading>
                <HStack spacing={2}>
                  <Button size="sm" colorScheme="gray" variant="outline"
                    onClick={refreshManagementData}
                    leftIcon={<EditIcon />}>
                    Refresh Data
                  </Button>
                  <Button size="sm" colorScheme="yellow" variant="outline"
                    onClick={() => setShowDebugPanel(!showDebugPanel)}>
                    {showDebugPanel ? 'Hide Debug' : 'Show Debug'}
                  </Button>
                </HStack>
              </HStack>

              {showDebugPanel && (
                <Box bg="gray.900" p={4} borderRadius="md" mb={4} fontSize="xs">
                  <VStack spacing={2} align="stretch">
                    <Text color="yellow.300" fontWeight="bold">Debug Panel</Text>
                    <Text color="gray.300">Last Refresh: {lastRefreshTime.toLocaleTimeString()}</Text>
                    <Text color="gray.300">Loading Data: {loadingManagement ? 'TRUE' : 'FALSE'}</Text>
                    <Text color="gray.300">Available Users Count: {availableUsers?.length || 0}</Text>
                    <Text color="gray.300">Legacy Cohort Users: {legacyCohortUsers?.length || 0}</Text>
                    <Text color="gray.300">Current Members Count: {currentMembers?.length || 0}</Text>
                    <Text color="gray.300">Orphan Memberships: {orphanMemberships?.length || 0}</Text>
                    <Text color="gray.300">Cohort ID: {cohort?.id}</Text>
                    <Text color="gray.300">Institution: {cohort?.institution}</Text>
                    <Text color="orange.300">Data Source: Direct queries</Text>
                  </VStack>
                </Box>
              )}

              {activeManagementError && (
                <Alert status="error" mb={4}>
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load cohort members: {activeManagementError.message}</Text>
                </Alert>
              )}

              {loadingManagement ? (
                <VStack spacing={4} align="center" py={8}>
                  <Spinner size="lg" color="blue.400" />
                  <Text color="gray.300">Loading member data and agent access information...</Text>
                  <Text fontSize="sm" color="gray.400">This may take a few seconds</Text>
                </VStack>
              ) : currentMembers.length === 0 && orphanMemberships.length === 0 ? (
                <Alert status="info"><AlertIcon /><Text>No members in this cohort yet.</Text></Alert>
              ) : (
                <VStack spacing={4} align="stretch" maxH="400px" overflowY="auto">
                  {orphanMemberships.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="orange.300" mb={2}>
                        Orphan Memberships ({orphanMemberships.length})
                      </Text>
                      <Text fontSize="xs" color="gray.400" mb={2}>
                        These rows have missing user identity data and are hidden from role sections below.
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(orphanMemberships, (membership) => (
                          <Flex key={`orphan-${membership.id}`} p={3} bg="orange.900" borderRadius="md" align="center" justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="bold">Membership {membership.id}</Text>
                              <Text fontSize="xs" color="gray.300">Role: {membership.primaryRole || "unknown"}</Text>
                              <Text fontSize="xs" color="gray.300">User ID: {membership.userId || "missing"}</Text>
                            </VStack>
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleRemoveOrphanMembership(membership.id)}
                              aria-label={`Remove orphan membership ${membership.id}`}
                            />
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  )}
                  {instructors.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="blue.300" mb={2}>
                        Instructors ({instructors.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(instructors, member => renderMemberRow(member, 'Instructor', 'blue.900'))}
                      </VStack>
                    </Box>
                  )}
                  {staff.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="green.300" mb={2}>
                        Staff ({staff.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(staff, member => renderMemberRow(member, 'Staff', 'green.900'))}
                      </VStack>
                    </Box>
                  )}
                  {students.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.300" mb={2}>
                        Students ({students.length})
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {safeArrayMap(students, member => renderMemberRow(member, 'Student', 'gray.700'))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              )}
            </Box>

            <Divider />

            {/* Add Members Section */}
            <Box>
              <Heading size="md" mb={4}>Add New Members</Heading>
              <VStack spacing={4} align="stretch">
                <Input placeholder="Search users by name or email..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} bg="gray.700" borderColor="gray.600" />
                {searchTerm && (
                  <Box>
                    <Text fontSize="sm" color="gray.300" mb={2}>
                      Available Users ({Array.isArray(availableToAdd) ? availableToAdd.length : 0})
                    </Text>
                    <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                      {safeArrayMap(availableToAdd, user => (
                        <HStack key={user.id} p={2} bg="gray.700" borderRadius="md">
                          <Checkbox isChecked={membersToAdd.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) setMembersToAdd(prev => [...prev, user.id]);
                              else setMembersToAdd(prev => prev.filter(id => id !== user.id));
                            }} />
                          <VStack align="start" spacing={0} flex="1">
                            <Text fontWeight="bold">{getUserDisplayName(user)}</Text>
                            {user?.email && user.email !== getUserDisplayName(user) && (
                              <Text fontSize="sm" color="gray.300">{user.email}</Text>
                            )}
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}
                {membersToAdd.length > 0 && (
                  <HStack>
                    <Button onClick={handleAddMembers} colorScheme="green" size="sm" leftIcon={<AddIcon />}>
                      Add {membersToAdd.length} Selected Member(s)
                    </Button>
                    <Button onClick={() => setMembersToAdd([])} variant="ghost" size="sm">Clear Selection</Button>
                  </HStack>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
